import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TimesheetService, TimesheetEntry, WorkCode } from '../services/timesheet.service';
import { forkJoin, of } from 'rxjs';
import { ProjectService, Project } from '../services/project.service';
import { AuthService } from '../services/auth.service';
import { SelectedProjectService } from '../services/selectedproject.service';
import { UserService, User } from '../services/user.service';

@Component({
  selector: 'app-timesheet-report',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatIcon],
  templateUrl: './timesheet-report.component.html',
  styleUrls: ['./timesheet-report.component.css']
})
export class TimesheetReportComponent implements OnInit {
  timesheetEntries: TimesheetEntry[] = [];
  displayedEntries: TimesheetEntry[] = [];
  workCodes: WorkCode[] = [];
  projects: Project[] = [];
  selectedProject: number | null = null;

  // Pagination
  pageSize = 10;
  currentPage = 1;
  totalPages = 1;

  // cache for usernames
  private usernameCache: Map<string, string> = new Map();
  // cache full user info when available
  private userInfoCache: Map<string, User | null> = new Map();
  
  loading = false;
  projectsLoading = false;
  error: string | null = null;
  
  // Filter modal state
  showFilterModal = false;
  // Filter criteria
  availableEmployees: Array<{ id: string; name: string }> = [];
  selectedEmployeeId: string | null = null;
  startDate: string | null = null;
  endDate: string | null = null;
  selectedWorkCodes: string[] = [];
  selectedJobTypes: string[] = [];
  availableJobTypes: string[] = [];
  allEntries: TimesheetEntry[] = [];

  
  constructor(
    private timesheetService: TimesheetService,
    private projectService: ProjectService,
    private authService: AuthService,
    private selectedProjectService: SelectedProjectService,
    private userService: UserService
  ) {}

  openFilterModal(): void { this.showFilterModal = true; }
  closeFilterModal(): void { this.showFilterModal = false; }

  ngOnInit(): void {
    this.loadProjects();
    // ensure work codes are loaded for the filter multi-select
    this.loadWorkCodes();

    const selectedProject = this.selectedProjectService.getSelectedProject();
    if (selectedProject) {
      this.selectedProject = selectedProject;
      this.loadTimesheetEntries();
    }
  }
  
  loadProjects(): void {
    this.projectsLoading = true;
    this.projectService.getAllProjects().subscribe({
      next: (projects) => {
        this.projects = projects;
        this.projectsLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to load projects';
        this.projectsLoading = false;
        console.error(err);
      }
    });
  }
  
  loadWorkCodes(): void {
    this.timesheetService.getWorkCodes().subscribe({
      next: (workCodes) => {
        this.workCodes = workCodes.sort((a, b) => a.displayOrder - b.displayOrder);
      },
      error: (err) => {
        this.error = 'Failed to load work codes';
        console.error(err);
      }
    });
  }
  
  loadTimesheetEntries(): void {
    if (!this.selectedProject) return;
    
    this.loading = true;
    this.error = null;
    
    this.timesheetService.getTimesheetByProject(this.selectedProject).subscribe({
      next: (entries) => {
        // sort descending by date
        this.allEntries = entries.slice().sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        // populate available job types and employees from entries
        const jobTypeSet = new Set<string>();
        const empSet = new Set<string>();
        this.allEntries.forEach(e => {
          // only include EWOs as job type options
          if (e.jobType && e.jobType.startsWith('EWO-')) jobTypeSet.add(e.jobType);
          if (e.employee) empSet.add(e.employee);
        });
        this.availableJobTypes = Array.from(jobTypeSet).sort();
        this.availableEmployees = [];
        empSet.forEach(empId => {
          // fetch username (async) - optimistic placeholder
          this.availableEmployees.push({ id: empId, name: 'Loading...' });
          this.userService.getUsernameById(empId).subscribe({
            next: (username) => {
              const idx = this.availableEmployees.findIndex(x => x.id === empId);
              if (idx >= 0) this.availableEmployees[idx].name = username;
            },
            error: () => {
              const idx = this.availableEmployees.findIndex(x => x.id === empId);
              if (idx >= 0) this.availableEmployees[idx].name = empId;
            }
          });
        });

        // start with no filters applied
        this.selectedEmployeeId = null;
        this.startDate = null;
        this.endDate = null;
        this.selectedWorkCodes = [];
        this.selectedJobTypes = [];

        // apply filters (initially none) which sets timesheetEntries + pagination
        this.applyFilters();
        this.loading = false;

        // setup pagination
        this.currentPage = 1;
        this.totalPages = Math.max(1, Math.ceil(this.timesheetEntries.length / this.pageSize));
        this.updateDisplayedEntries();

        if (this.timesheetEntries.length === 0) {
          this.error = 'No timesheet entries found for this project.';
        }
      },
      error: (err) => {
        this.error = typeof err.error === 'string' ? err.error : 'Error fetching timesheet data.';
        this.loading = false;
        this.allEntries = [];
        this.timesheetEntries = [];
        this.displayedEntries = [];
      }
    });
  }

  private updateDisplayedEntries(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.displayedEntries = this.timesheetEntries.slice(start, start + this.pageSize);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updateDisplayedEntries();
  }

  nextPage(): void { this.goToPage(this.currentPage + 1); }
  prevPage(): void { this.goToPage(this.currentPage - 1); }

  applyFilters(): void {
    // Start from all entries
    let filtered = this.allEntries.slice();

    if (this.selectedEmployeeId) {
      filtered = filtered.filter(e => e.employee === this.selectedEmployeeId);
    }

    if (this.startDate) {
      const s = new Date(this.startDate);
      filtered = filtered.filter(e => new Date(e.date) >= s);
    }

    if (this.endDate) {
      const ed = new Date(this.endDate);
      // include the entire end day
      ed.setHours(23,59,59,999);
      filtered = filtered.filter(e => new Date(e.date) <= ed);
    }

    if (this.selectedWorkCodes && this.selectedWorkCodes.length > 0) {
      filtered = filtered.filter(e => this.selectedWorkCodes.includes(e.workCode));
    }

    if (this.selectedJobTypes && this.selectedJobTypes.length > 0) {
      filtered = filtered.filter(e => (e.jobType ? this.selectedJobTypes.includes(e.jobType) : false));
    }

    // assign filtered to timesheetEntries and reset pagination
    this.timesheetEntries = filtered.slice();
    this.currentPage = 1;
    this.totalPages = Math.max(1, Math.ceil(this.timesheetEntries.length / this.pageSize));
    this.updateDisplayedEntries();
    this.showFilterModal = false;
  }

  resetFilters(): void {
    // Clear all filter criteria and show all entries (without closing modal)
    this.selectedEmployeeId = null;
    this.startDate = null;
    this.endDate = null;
    this.selectedWorkCodes = [];
    this.selectedJobTypes = [];

    // Reset to full entry set and refresh pagination/display
    this.timesheetEntries = this.allEntries.slice();
    this.currentPage = 1;
    this.totalPages = Math.max(1, Math.ceil(this.timesheetEntries.length / this.pageSize));
    this.updateDisplayedEntries();
  }
  
  getProjectName(projectNumber: number): string {
    const project = this.projects.find(p => p.projectNumber === projectNumber);
    return project ? project.projectName : `Project #${projectNumber}`;
  }

  getWorkCodeDescription(code: string): string {
    const wc = this.workCodes.find(w => w.code === code);
    return wc ? wc.description : code;
  }

  private escapeCsvField(value: any): string {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  private formatDateForCsv(dateStr: string | undefined | null): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate().toString().padStart(2, '0');
    const mon = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day}/${mon}/${year}`;
  }

  downloadCsv(): void {
    // Use the filtered entries (timesheetEntries) as the current state
    const rows = this.timesheetEntries.slice();

    // Collect unique employee IDs that we may need to fetch full user records for
    const ids = Array.from(new Set(rows.map(r => r.employee)));
    const missingUserIds = ids.filter(id => !this.userInfoCache.has(id));

    const fetches = missingUserIds.map(id => this.userService.getUserById(id));

    const proceedWithUsers = (users: User[]) => {
      // cache fetched users
      missingUserIds.forEach((id, idx) => this.userInfoCache.set(id, users[idx] || null));

      // Build CSV with requested columns
      const header = ['Project Number', 'Date', 'Work Code', 'Job Type', 'Employee login', 'Employee Name', 'Company', 'Hours'];
      const lines = [header.map(h => this.escapeCsvField(h)).join(',')];

      rows.forEach(r => {
        const date = this.formatDateForCsv(r.date);
        const wc = this.getWorkCodeDescription(r.workCode);
        const jt = r.jobType || '';
        const user = this.userInfoCache.get(r.employee) || null;
        const login = user?.username || this.usernameCache.get(r.employee) || r.employee;
        const name = user ? `${user.firstname || ''} ${user.lastname || ''}`.trim() : (this.usernameCache.get(r.employee) || r.employee);
        // company field may be present under different keys; attempt common ones
        // @ts-ignore - allow reading optional properties not in the interface
        const company = (user && ((user as any).company || (user as any).company_id || (user as any).companyId)) || '';
        const hrs = r.hours != null ? r.hours : '';
        const fields = [r.project, date, wc, jt, login, name, company, hrs];
        lines.push(fields.map(f => this.escapeCsvField(f)).join(','));
      });

      // Append a totals row for hours
      const totalHours = rows.reduce((sum, entry) => sum + (entry.hours || 0), 0);
      const totalRow = ['', '', '', '', '', '', 'Total', totalHours];
      lines.push(totalRow.map(f => this.escapeCsvField(f)).join(','));

      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const now = new Date();
      const day = now.getDate().toString().padStart(2, '0');
      const mon = now.toLocaleString('en-US', { month: 'short' });
      const year = now.getFullYear();
      const fname = `${this.selectedProject} - Timesheet Report ${day}-${mon}-${year}.csv`;
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    };

    if (fetches.length > 0) {
      forkJoin(fetches).subscribe({ next: proceedWithUsers, error: () => proceedWithUsers([]) });
    } else {
      // nothing to fetch; still proceed
      proceedWithUsers([]);
    }
  }

  calculateTotalHours(): number {
    return this.timesheetEntries.reduce((sum, entry) => sum + entry.hours, 0);
  }

  getUsernameById(userId: string): string {
    // Check if we already have this username in cache
    if (this.usernameCache.has(userId)) {
      return this.usernameCache.get(userId) || 'Unknown';
    }
    // If not a GUID, use the value directly as a display name
    const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidPattern.test(userId)) {
      this.usernameCache.set(userId, userId);
      return userId;
    }
    
    // Not in cache, set a placeholder and fetch
    this.usernameCache.set(userId, 'Loading...');
    
    this.userService.getUsernameById(userId).subscribe({
      next: (username) => {
        // Update cache when the API call succeeds
        this.usernameCache.set(userId, username);
      },
      error: (err) => {
        console.error(`Failed to load username for ID ${userId}`, err);
        this.usernameCache.set(userId, 'Error loading');
      }
    });
    
    return this.usernameCache.get(userId) || 'Unknown';
  }

  
}